import axios from 'axios';
import { formatLocalTodayYmd } from '../utils/dateUtils';

const API_BASE = '/api/quotes';

export const quoteService = {
  // Get all quotes with optional filters (pass { signal } for AbortController)
  getAll: async (params = {}, axiosConfig = {}) => {
    const response = await axios.get(API_BASE, { params, ...axiosConfig });
    return response.data;
  },

  // Get single quote by ID
  getById: async (id) => {
    const response = await axios.get(`${API_BASE}/${id}`);
    return response.data;
  },

  // Get next quote number (display format 260001; pass data.quote_number when creating)
  getNextNumber: async () => {
    const response = await axios.get(`${API_BASE}/next-number`);
    return response.data;
  },

  // Create new quote
  create: async (quoteData) => {
    const response = await axios.post(API_BASE, quoteData);
    return response.data;
  },

  // Update quote
  update: async (id, quoteData) => {
    const response = await axios.put(`${API_BASE}/${id}`, quoteData);
    return response.data;
  },

  // Delete quote
  delete: async (id) => {
    const response = await axios.delete(`${API_BASE}/${id}`);
    return response.data;
  },

  duplicate: async (id) => {
    const response = await axios.post(`${API_BASE}/${id}/duplicate`);
    return response.data;
  },

  // Add line item
  addLineItem: async (quoteId, itemData) => {
    const response = await axios.post(`${API_BASE}/${quoteId}/line-items`, itemData);
    return response.data;
  },

  // Update line item
  updateLineItem: async (quoteId, itemId, itemData) => {
    const response = await axios.put(`${API_BASE}/${quoteId}/line-items/${itemId}`, itemData);
    return response.data;
  },

  // Delete line item
  deleteLineItem: async (quoteId, itemId) => {
    const response = await axios.delete(`${API_BASE}/${quoteId}/line-items/${itemId}`);
    return response.data;
  },

  // Convert quote to order (send local calendar date so it matches the browser, not the server's UTC day)
  convertToOrder: async (quoteId, extra = {}) => {
    const response = await axios.post(`${API_BASE}/${quoteId}/convert`, {
      order_date: formatLocalTodayYmd(),
      ...extra
    });
    return response.data;
  },

  // Replace draft line items in one request
  replaceDraftLineItems: async (quoteId, lineItems) => {
    const response = await axios.put(`${API_BASE}/${quoteId}/draft-line-items`, { lineItems });
    return response.data;
  },

  // Get full revision chain for a quote (current quote + all predecessors)
  getRevisions: async (quoteId) => {
    const response = await axios.get(`${API_BASE}/${quoteId}/revisions`);
    return response.data;
  },

  /** Quote split / partial / revision family and linked orders */
  getLineage: async (quoteId) => {
    const response = await axios.get(`${API_BASE}/${quoteId}/lineage`);
    return response.data;
  },

  /** Work orders linked via quote_id (count + list for UI) */
  getRelatedOrders: async (quoteId) => {
    const response = await axios.get(`${API_BASE}/${quoteId}/related-orders`);
    return response.data;
  },

  /** Download Field Work / Service proposal PDF (scope_of_work quotes only). Returns blob. */
  downloadProposalPdf: async (quoteId) => {
    const response = await axios.get(`${API_BASE}/${quoteId}/proposal-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  }
};
